import type { LinkProps } from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { Button } from './button';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';

export function Pagination({
  className,
  'aria-label': arialLabel = 'Page navigation',
  ...props
}: JSX.IntrinsicElements['nav']) {
  return (
    <nav
      role="navigation"
      aria-label={arialLabel}
      className={twMerge('mx-auto flex w-full justify-center gap-x-2', className)}
      {...props}
    />
  );
}

export function PaginationList({ className, ...props }: JSX.IntrinsicElements['div']) {
  return <div {...props} className={twMerge('flex hidden gap-x-1 sm:flex', className)} />;
}

export function PaginationPrevious({
  className,
  label = 'Previous',
  isDisabled,
  ...props
}: LinkProps & { className?: string; label?: string }) {
  return (
    <Button asChild variant="plain">
      <a
        {...props}
        data-disabled={isDisabled}
        className={twMerge(
          'px-3.5 outline-offset-0 hover:no-underline data-[disabled="true"]:opacity-50 data-[disabled="true"]:cursor-not-allowed',
          className,
        )}
      >
        <ChevronLeftIcon />

        {label}
      </a>
    </Button>
  );
}

export function PaginationNext({
  className,
  label = 'Next',
  isDisabled,
  ...props
}: LinkProps & { className?: string; label?: string }) {
  return (
    <Button asChild variant="plain">
      <a
        {...props}
        data-disabled={isDisabled}
        className={twMerge(
          'px-3.5 outline-offset-1 hover:no-underline data-[disabled="true"]:opacity-50 data-[disabled="true"]:cursor-not-allowed',
          className,
        )}
      >
        {label}
        <ChevronRightIcon />
      </a>
    </Button>
  );
}

export function PaginationPage({
  className,
  current,
  'aria-label': arialLabel,
  isDisabled,
  isLoading,
  ...props
}: LinkProps & { className?: string; current?: boolean; children: string; isLoading?: boolean }) {
  return (
    <Button asChild {...(!current && { variant: 'plain' })}>
      <a
        {...props}
        data-disabled={isDisabled}
        data-loading={isLoading}
        aria-label={arialLabel ?? `Page ${props.children}`}
        className={twMerge(
          'min-w-9 outline-offset-1 hover:no-underline data-[loading="true"]:animate-pulse data-[disabled="true"]:cursor-not-allowed data-[disabled="true"]:opacity-50',
          className,
        )}
      />
    </Button>
  );
}

export function PaginationGap({ className, ...props }: JSX.IntrinsicElements['span']) {
  return (
    <span {...props} aria-hidden className={twMerge('h-9 px-3.5 select-none', className)}>
      &hellip;
    </span>
  );
}
